import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import type translations from "../translations/translations.json";
import type { AppDispatch, RootState } from "../store";
import { fetchOperationsTeams } from "../store/features/operationsTeamSlice";
import { searchOrdersApi } from "../api/ordersApi";
import { searchStandbyOrdersApi } from "../api/standbyOrdersApi";
import { createUangJalanReportApi } from "../api/uangJalanReportsApi";
import DetailPageHeader from "../components/common/DetailPageHeader";
import DetailSectionCard from "../components/common/DetailSectionCard";
import { TestId } from "../constants/testIds";
import { canMutateOrders, isFinance, isUserAdmin } from "../types/user";
import { calculateOrderExpenseTotal, OrderStatus, type Order } from "../types/order";
import type { StandbyOrder } from "../types/standbyOrder";
import { getErrorMessage } from "../utils/errorHandling";
import { formatRupiah } from "../utils/formatRupiah";
import { toastEmitter } from "../utils/toastEmitter";

type TranslationKeys = keyof typeof translations.en;

const headerButtonSx = {
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: 1.5,
};

const UangJalanReportCreatePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const { operationsTeams } = useSelector((state: RootState) => state.operationsTeams);

  const canSelectTeam = isUserAdmin(user);
  const memberTeams = useMemo(() => {
    if (!user || canSelectTeam) {
      return operationsTeams;
    }

    return operationsTeams.filter((team) => team.userIds.includes(user._id));
  }, [operationsTeams, user, canSelectTeam]);

  const [teamOperationsId, setTeamOperationsId] = useState("");
  const [readyOrders, setReadyOrders] = useState<Order[]>([]);
  const [readyStandbyOrders, setReadyStandbyOrders] = useState<StandbyOrder[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [selectedStandbyOrderIds, setSelectedStandbyOrderIds] = useState<string[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchOperationsTeams());
  }, [dispatch]);

  useEffect(() => {
    if (canSelectTeam || memberTeams.length === 0 || teamOperationsId) {
      return;
    }

    setTeamOperationsId(memberTeams[0]._id);
  }, [canSelectTeam, memberTeams, teamOperationsId]);

  useEffect(() => {
    if (!teamOperationsId) {
      setReadyOrders([]);
      setReadyStandbyOrders([]);
      setSelectedOrderIds([]);
      setSelectedStandbyOrderIds([]);
      return;
    }

    const loadReadyOrders = async () => {
      setLoadingOrders(true);
      setError(null);
      try {
        const [{ orders }, { standbyOrders }] = await Promise.all([
          searchOrdersApi(
            {
              teamOperationsId,
              status: OrderStatus.READY_FOR_REVIEW,
            },
            { page: 1, pageSize: 100 },
          ),
          searchStandbyOrdersApi(
            {
              teamOperationsId,
              status: OrderStatus.READY_FOR_REVIEW,
            },
            { page: 1, pageSize: 100 },
          ),
        ]);
        setReadyOrders(orders);
        setReadyStandbyOrders(standbyOrders);
        setSelectedOrderIds([]);
        setSelectedStandbyOrderIds([]);
      } catch (err: any) {
        setError(getErrorMessage(err, t, "fetchOrders"));
      } finally {
        setLoadingOrders(false);
      }
    };

    loadReadyOrders();
  }, [teamOperationsId, t]);

  if (!user) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isFinance(user) || !canMutateOrders(user)) {
    return <Navigate to="/uang-jalan-reports" replace />;
  }

  const teamOptions = memberTeams.map((team) => ({
    value: team._id,
    label: team.name,
  }));

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds((current) =>
      current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId],
    );
  };

  const toggleStandbySelection = (orderId: string) => {
    setSelectedStandbyOrderIds((current) =>
      current.includes(orderId) ? current.filter((id) => id !== orderId) : [...current, orderId],
    );
  };

  const handleSubmit = async () => {
    if (!teamOperationsId) {
      setError(t("reports.validation.teamRequired" as TranslationKeys));
      return;
    }

    if (selectedOrderIds.length === 0 && selectedStandbyOrderIds.length === 0) {
      setError(t("reports.validation.membersRequired" as TranslationKeys));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const report = await createUangJalanReportApi({
        teamOperationsId,
        ...(selectedOrderIds.length > 0 ? { orderIds: selectedOrderIds } : {}),
        ...(selectedStandbyOrderIds.length > 0 ? { standbyOrderIds: selectedStandbyOrderIds } : {}),
      });
      toastEmitter.emit(t("reports.createdSuccessfully" as TranslationKeys), "success");
      navigate(`/uang-jalan-reports/${report.readableId}`);
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, "createUangJalanReport");
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCount = selectedOrderIds.length + selectedStandbyOrderIds.length;

  return (
    <Box sx={{ pb: 4 }} data-testid={TestId.UJR.CREATE_FORM}>
      <DetailPageHeader
        title={t("reports.createTitle" as TranslationKeys)}
        subtitle={t("reports.selectMembers" as TranslationKeys)}
        actions={
          <>
            <Button
              variant="outlined"
              onClick={() => navigate("/uang-jalan-reports")}
              sx={headerButtonSx}
            >
              {t("Cancel" as TranslationKeys)}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || selectedCount === 0}
              data-testid={TestId.UJR.SUBMIT}
              sx={headerButtonSx}
            >
              {t("Create Report" as TranslationKeys)}
              {selectedCount > 0 ? ` (${selectedCount})` : ""}
            </Button>
          </>
        }
      />

      <DetailSectionCard
        title={t("reports.teamSection" as TranslationKeys)}
        icon={<GroupsOutlinedIcon fontSize="small" />}
        iconColor="#3B82F6"
        sx={{ mb: 2, maxWidth: 720 }}
      >
        <Autocomplete
          value={teamOptions.find((option) => option.value === teamOperationsId) || null}
          onChange={(_, newValue) => {
            setTeamOperationsId(newValue?.value || "");
          }}
          options={teamOptions}
          getOptionLabel={(option) => option.label}
          disabled={!canSelectTeam && memberTeams.length <= 1}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Team" as TranslationKeys)}
              required
              inputProps={{
                ...params.inputProps,
                "data-testid": TestId.UJR.TEAM,
              }}
            />
          )}
          size="small"
        />
      </DetailSectionCard>

      <DetailSectionCard
        title={t("reports.selectOrders" as TranslationKeys)}
        icon={<ChecklistOutlinedIcon fontSize="small" />}
        iconColor="#64748B"
        sx={{ mb: 2, maxWidth: 720 }}
      >
        {loadingOrders ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : readyOrders.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("reports.noReadyOrders" as TranslationKeys)}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {readyOrders.map((order) => (
              <FormControlLabel
                key={order._id}
                sx={{
                  mx: 0,
                  py: 1,
                  px: 1,
                  borderRadius: 1.5,
                  alignItems: "flex-start",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                control={
                  <Checkbox
                    checked={selectedOrderIds.includes(order._id)}
                    onChange={() => toggleOrderSelection(order._id)}
                    slotProps={{
                      input: {
                        "data-testid": TestId.UJR.ORDER_CHECKBOX(order._id),
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={
                  <Box sx={{ pt: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                    >
                      {order.readableId}
                      {order.externalOrderId ? ` · ${order.externalOrderId}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {order.customer?.name || "—"} ·{" "}
                      {formatRupiah(calculateOrderExpenseTotal(order))}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        )}
      </DetailSectionCard>

      <DetailSectionCard
        title={t("reports.selectStandbyOrders" as TranslationKeys)}
        icon={<ChecklistOutlinedIcon fontSize="small" />}
        iconColor="#0F766E"
        sx={{ maxWidth: 720 }}
      >
        {loadingOrders ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : readyStandbyOrders.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t("reports.noReadyStandbyOrders" as TranslationKeys)}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {readyStandbyOrders.map((order) => (
              <FormControlLabel
                key={order._id}
                sx={{
                  mx: 0,
                  py: 1,
                  px: 1,
                  borderRadius: 1.5,
                  alignItems: "flex-start",
                  "&:hover": { bgcolor: "action.hover" },
                }}
                control={
                  <Checkbox
                    checked={selectedStandbyOrderIds.includes(order._id)}
                    onChange={() => toggleStandbySelection(order._id)}
                    slotProps={{
                      input: {
                        "data-testid": TestId.UJR.STANDBY_CHECKBOX(order._id),
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                }
                label={
                  <Box sx={{ pt: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                    >
                      {order.readableId}
                      {order.date ? ` · ${order.date}` : ""}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {order.driver?.name || "—"} · {formatRupiah(order.totalPaid)}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DetailSectionCard>
    </Box>
  );
};

export default UangJalanReportCreatePage;
